import { useState, useEffect } from 'react';
import http from '../../../components/http/http';
import { getHashObj } from '../../../components/getSubjectId';
import {
  UrlParams,
  UserInfo,
  Biomarker,
  ReportRequest,
  ReportResponse
} from '../types';
import { DEBUG_MODE } from '../constants';
import {
  parseUserInfoFromUrl,
  parseBiomarkersFromStorage,
  getUploadedFileInfo
} from '../utils';
import debugData from '../report.json';

export const useReport = () => {
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [biomarkers, setBiomarkers] = useState<Biomarker[]>([]);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<{ fileName: string; hash: string } | null>(null);

  const generateReport = async () => {
    try {
      console.log('generateReport called');
      setLoading(true);
      setError(null);

      const urlParams = getHashObj() as UrlParams;
      console.log('urlParams:', urlParams);

      let surveyDefinitions: any[] = [];
      try {
        const surveyResponse = await http.get('/api/lab-results/surveys');
        let data = surveyResponse;
        if (surveyResponse && surveyResponse.data) {
          data = surveyResponse.data;
        }

        if (Array.isArray(data)) {
          surveyDefinitions = data;
        } else if (data && Array.isArray(data.data)) {
          surveyDefinitions = data.data;
        }
      } catch (e) {
        console.error('Failed to fetch survey definitions', e);
      }

      const userInfoData = parseUserInfoFromUrl(urlParams, surveyDefinitions);
      setUserInfo(userInfoData);

      const biomarkersData = parseBiomarkersFromStorage();
      setBiomarkers(biomarkersData);

      const requestData: ReportRequest = {
        biomarkers: biomarkersData,
        userInfo: {
          age: userInfoData.age,
          gender: userInfoData.gender,
          race: userInfoData.race,
          theme: userInfoData.theme || '',
          ...(userInfoData.surveyResponses && userInfoData.surveyResponses.length > 0 && { surveyResponses: userInfoData.surveyResponses })
        }
      };

      const fileInfo = getUploadedFileInfo();
      if (fileInfo) {
        (requestData as any).fileName = fileInfo.fileName;
        (requestData as any).fileHash = fileInfo.hash;
      }

      let response: ReportResponse;

      response = await http.post('/api/lab-results/adapt-to-analysis', requestData);

      setReportData(response);
    } catch (err: any) {
      console.error('Error generating report:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fileInfo = getUploadedFileInfo();
    setUploadedFileInfo(fileInfo);
  }, []);

  useEffect(() => {
    generateReport();
  }, []);

  return {
    reportData,
    loading,
    error,
    userInfo,
    biomarkers,
    generateReport,
    uploadedFileInfo
  };
};
